/**
 * Canonical dashboard property category.
 *
 * Properties carry two independent fields:
 *   - `purpose`        — zoning/use (residential, commercial, industrial,
 *                        agricultural, multi_purpose, …)
 *   - `property_type`  — physical sub-type (land, villa, apartment, factory, …)
 *
 * The dashboard "Properties" breakdown and the Properties-page filter present
 * five buckets: Residential, Commercial, Industrial, Land, Mixed.
 *
 * "Land" is driven by the **property_type**, not the purpose: a vacant land plot
 * is "Land" even when its purpose is industrial or multi_purpose. Without this,
 * land plots zoned for other uses were silently counted under their purpose
 * bucket and the Land bucket read 0. Agricultural-purpose properties (e.g. a
 * farm) also fall under Land, preserving the legacy `agricultural → land`
 * mapping. Land takes precedence over every purpose so each property lands in
 * exactly one bucket.
 */
export type PropertyCategory =
  | "residential"
  | "commercial"
  | "industrial"
  | "land"
  | "mixed"
  | "other";

export interface PropertyCategoryInput {
  purpose?: string | null;
  property_type?: string | null;
}

export function categorizeProperty(p: PropertyCategoryInput): PropertyCategory {
  if (p.property_type === "land" || p.purpose === "agricultural") return "land";
  switch (p.purpose) {
    case "residential":
      return "residential";
    case "commercial":
      return "commercial";
    case "industrial":
      return "industrial";
    case "multi_purpose":
      return "mixed";
    default:
      return "other";
  }
}
